import React, { useState } from "react";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import { graphql } from "gatsby";

import GridSection from "../components/GridSection";
import Layout from "../components/Layout";
import Marquee from "../components/Marquee";
import ImageText from "../components/ImageText";
import Tab from "../components/Tab";
import TabCardV2 from "../components/TabCardV2";
import TabCardGroup from "../components/TabCardGroup";
import Hero from "../components/Hero";
import Link from "../components/Link";

import CardsCarousel from "../components/CardsCarousel";
import HeroTitle from "../components/HeroTitle";
import StargazerImageText from "../components/StargazerImageText";

import InternalLinks from "../utils/internalLinks";
import ExternalLinks from "../utils/externalLinks";

import { useNetworkStats } from "../hooks/useNetworkStats";

import RightArrow from "../img/right-arrow.svg";
import faster from "../img/home/faster.png";
import dtmLite from "../img/home/dtm_lite.png";
import stargazerC14 from "../img/stargazer-wallet/stargazer-c14.png";

import AsteriskIcon from "../img/icons/asterisk-filled.svg";
import CircleIcon from "../img/icons/circle-dashed-filled.svg";
import GraphIcon from "../img/icons/graph-filled.svg";
import StarIcon from "../img/icons/star.svg";
import InfinityIcon from "../img/icons/infinity-filled.svg";
import ShieldIcon from "../img/icons/shield-filled.svg";

import StargazerLogo from "../img/icons/stargazer-filled.svg";
import DtmLogo from "../img/icons/dtm-filled.svg";
import DagExplorerLogo from "../img/icons/dag-explorer-filled.svg";
import DigitalEvidenceLogo from "../img/logos/digital-evidence-green.svg";
import FederalSolutionsLogo from "../img/logos/federal-logo-blue.svg";

import {
  IndexContainer,
  FirstSection,
  IntroducingDTMLite,
  MarqueeSection,
  MarqueeContainer,
  GrowingEcosystemMessage,
  SecondSection,
  ThirdSection,
  FourthSection,
  CardsContainer,
  CarouselSection,
  AmericasTrustedBlockchain,
  StargazerImageStyle,
  StargazerImageContainer,
} from "./styles/index-page-styles";

/**
 *
 * @param {{
 *  news_carousel: {
 *    title: string;
 *    content: string;
 *    img_url: {
 *      publicURL?: string;
 *    } | null;
 *    button_content: string;
 *    button_link: string
 *  }[]
 * }} props
 * @returns
 */
const IndexPageTemplate = ({ news_carousel }) => {
  const [coreFeaturesPressed, setCoreFeaturesPressed] = useState(false);
  const [ourProductsPressed, setOurProductsPressed] = useState(true);
  const intl = useIntl();

  const {
    data: networkStats
  } = useNetworkStats();

  const tabTitles = [
     {
      header: intl.formatMessage({ id: "home_page.products" }),
      onPress: () => {
        setCoreFeaturesPressed(false);
        setOurProductsPressed(true);
      },
      pressed: ourProductsPressed,
    },
    {
      header: intl.formatMessage({ id: "home_page.features_home" }),
      onPress: () => {
        setCoreFeaturesPressed(true);
        setOurProductsPressed(false);
      },
      pressed: coreFeaturesPressed,
    },
  ];

  const coreFeaturesCards = [
    {
      title: intl.formatMessage({ id: "home_page.secure_and_resilient_title" }),
      description: intl.formatMessage({
        id: "home_page.secure_and_resilient_description",
      }),
      iconComponent: ShieldIcon,
    },
     {
      title: intl.formatMessage({ id: "home_page.scalable_title" }),
      description: intl.formatMessage({
        id: "home_page.scalable_description",
      }),
      iconComponent: InfinityIcon,
    },
    {
      title: intl.formatMessage({ id: "home_page.feeless_title" }),
      description: intl.formatMessage({ id: "home_page.feeless_description" }),
      iconComponent: CircleIcon,
    },
    {
      title: intl.formatMessage({ id: "home_page.descentralized_title" }),
      description: intl.formatMessage({
        id: "home_page.descentralized_description",
      }),
      iconComponent: GraphIcon,
    },
    {
      title: intl.formatMessage({ id: "home_page.interoperable_title" }),
      description: intl.formatMessage({
        id: "home_page.interoperable_description",
      }),
      iconComponent: AsteriskIcon,
    },
    {
      title: intl.formatMessage({ id: "home_page.customizable_title" }),
      description: intl.formatMessage({
        id: "home_page.customizable_description",
      }),
      iconComponent: DagExplorerLogo,
    }
  ];

  const ourProductsCards = [
    {
      title: intl.formatMessage({ id: "home_page.digital_evidence_title" }),
      description: intl.formatMessage({
        id: "home_page.digital_evidence_description",
      }),
      iconComponent: DigitalEvidenceLogo,
      link: InternalLinks.DIGITAL_EVIDENCE
    },
        {
      title: intl.formatMessage({ id: "home_page.dor_traffic_miner_title" }),
      description: intl.formatMessage({
        id: "home_page.dor_traffic_miner_description",
      }),
      iconComponent: DtmLogo,
      link: InternalLinks.DTM,
    },
    {
      title: intl.formatMessage({ id: "home_page.federal_solutions_title" }),
      description: intl.formatMessage({
        id: "home_page.federal_solutions_description",
      }),
      iconComponent: FederalSolutionsLogo,
      link: InternalLinks.FEDERAL,
    },
    {
      title: intl.formatMessage({ id: "home_page.stargazer_wallet_title" }),
      description: intl.formatMessage({
        id: "home_page.stargazer_wallet_description",
      }),
      iconComponent: StargazerLogo,
      link: InternalLinks.STARGAZER_WALLET,
    }
  ];

  const heroContent = [
    {
      title: intl.formatMessage({ id: "home_page.snapshots" }),
      value: networkStats?.totalSnapshots,
      useInterrogation: true,
      tooltipMessage:
        "Total number of snapshots",
    },
    {
      title: intl.formatMessage({ id: "home_page.staked_in_dag" }),
      value: networkStats?.totalSupply,
      useInterrogation: true,
      tooltipMessage:
        "Total supply of DAG.",
    },
    {
      title: intl.formatMessage({ id: "home_page.dag_wallet_holders" }),
      value: networkStats?.totalWallets,
      useInterrogation: true,
      tooltipMessage: "Total number of DAG wallet holders.",
    },
    {
      title: intl.formatMessage({ id: "home_page.validator_nodes" }),
      value: networkStats?.validatorNodes,
      useInterrogation: true,
      tooltipMessage:
        "The total number of validator nodes running on Mainnet 1.0 and Testnet 2.0.",
    },
  ];

  const IntroducingDTMLiteComponent = () => {
    return (
      <IntroducingDTMLite>
        <strong>
          {intl.formatMessage({
            id: "home_page.introducing_dtm_lite",
          })}
        </strong>
        <p>
          {intl.formatMessage({
            id: "home_page.install_mine",
          })}
        </p>
        <p>
          {intl.formatMessage({
            id: "home_page.earn",
          })}{" "}
          <span>$DOR</span>.
        </p>
      </IntroducingDTMLite>
    );
  };

  return (
    <>
      <Layout theme="dark" title="Constellation Network">
        <IndexContainer fluid>
          <GridSection>
          <AmericasTrustedBlockchain>
            <div>
              <StarIcon />
              <span>America's Trusted Blockchain</span>
              <StarIcon />
            </div>
          </AmericasTrustedBlockchain>
          <HeroTitle
            subTitle={intl.formatMessage({ id: "home_page.hero_subtitle" })}
            buttons={[
              { content: intl.formatMessage({ id: "home_page.hero_cta_button" }), linkTo: "/digital-evidence", icon: <RightArrow /> }
            ]}
          >
            {intl.formatMessage({ id: "home_page.hero_title_pt1" })}
            <br/>
            {intl.formatMessage({ id: "home_page.hero_title_pt2" })}
          </HeroTitle>
        </GridSection>
        <GridSection startsOnFirstColumn>
          <MarqueeSection>
            <GrowingEcosystemMessage>
              <FormattedMessage id="home_page.our_ecosystem_partners" />
            </GrowingEcosystemMessage>
            <MarqueeContainer>
              <Link to={InternalLinks.COMMUNITY}>
                <Marquee backgroundColor="transparent" height="96px" />
              </Link>
            </MarqueeContainer>
          </MarqueeSection>
        </GridSection>
        <GridSection>
          <CarouselSection>
            <h2>News and Events</h2>
            <CardsCarousel
              cards={news_carousel.map((news, idx) => (
                <CardsCarousel.Card
                  key={idx}
                  title={news.title}
                  content={news.content}
                  imgUrl={news.img_url?.publicURL}
                  button={{
                    content: news.button_content,
                    linkTo: news.button_link,
                  }}
                />
              ))}
            />
          </CarouselSection>
        </GridSection>
        <GridSection>
          <FourthSection>
            <h1>
              <FormattedMessage id="home_page.growing_ecosystem" />
            </h1>
            <Tab titles={tabTitles} />
            <CardsContainer>
               {ourProductsPressed &&
                <TabCardGroup 
                  noPadding>
                  {ourProductsCards.map((card) => (
                    <TabCardV2
                      key={card.title}
                      description={card.description}
                      title={card.title}
                      iconComponent={card.iconComponent}
                      link={card.link}
                      height="auto"
                  />
                  ))}
                </TabCardGroup>
              }
              {coreFeaturesPressed &&
                <TabCardGroup
                  noPadding>
                    {coreFeaturesCards.map((card) => (
                      <TabCardV2
                        key={card.title}
                        description={card.description}
                        title={card.title}
                        iconComponent={card.iconComponent}
                        link={card.link}
                        height="auto"
                      />
                   ))}
                </TabCardGroup>
               } 
            </CardsContainer>
          </FourthSection>
        </GridSection>
        <GridSection>
          <StargazerImageContainer>
            <StargazerImageText
              title={"On-ramp with C14 using Apple Pay"}
              content={
                "Download our multi-chain Stargazer wallet that offers native support to Constellation Network. You will be able to buy metagraph tokens, DAG and manage all your assets in a non-custodial and secure way."
              }
              image={
                <StargazerImageStyle>
                  <img
                    src={stargazerC14}
                    alt="stargazer-c14"
                    style={{ width: "920px" }}
                  />
                </StargazerImageStyle>
              }
            />
          </StargazerImageContainer>
        </GridSection>
        <GridSection>
          <SecondSection>
            <ImageText
              aboveTitleMessage={intl.formatMessage({
                id: "home_page.meet_constellation_title",
              })}
              descriptions={[
                {
                  text: intl.formatMessage({
                    id: "home_page.meet_constellation_description",
                  }),
                },
              ]}
              imageSrc={faster}
              title={intl.formatMessage({ id: "home_page.go_from_layer" })}
              highlightWord={intl.formatMessage({ id: "home_page.sixty" })}
              buttonMessage={intl.formatMessage({ id: "home_page.learn_more" })}
              hoverButtonColor="#1552EF"
              redirectLink={ExternalLinks.DEVDOCS}
              redirectLinkExternal
              formatImage={true}
              imageCustomWidth="570px"
              imageCustomHeight="648px"
              imageLeft="600px"
              fitImage
            />
          </SecondSection>
        </GridSection>
        <GridSection>
          <ThirdSection>
            <Hero content={heroContent}/>
          </ThirdSection>
        </GridSection>
        <GridSection>
          <FirstSection lastSection={true}>
            <ImageText
              imageSrc={dtmLite}
              CustomTitleComponent={IntroducingDTMLiteComponent}
              customTextWidth="60%"
              buttonMessage={intl.formatMessage({
                id: "home_page.buy",
              })}
              redirectLink={ExternalLinks.BUY_DTM_LITE}
              redirectLinkExternal
              formatImage={true}
              customPrimaryButtonBackgroundColor="#6938EF"
              additionalButtonMessage={intl.formatMessage({
                id: "home_page.transactions",
              })}
              titleMaxWidth="100%"
              imageCustomWidth="692px"
              imageLeft="470px"
              customAdditionalButtonColor="#829EF7"
              customAdditionalButtonBorder="1.5px solid var(--brand-colors-constellation-blue-400, #829EF7)"
              customAdditionalButtonMargin="0px 0px 0px 24px"
              customAdditionalButtonWidth="225px"
              additionalButtonRedirectLink={ExternalLinks.DOR_TRANSACTIONS}
              additionalButtonRedirectLinkExternal
            />
          </FirstSection>
        </GridSection>
      </IndexContainer>
    </Layout>
    </>
  );
};

function IndexPage({ data }) {
  const { frontmatter } = data.markdownRemark;

  console.dir(frontmatter);

  return <IndexPageTemplate news_carousel={frontmatter.news_carousel} />;
}

export default IndexPage;

export const pageQuery = graphql`
  query IndexPageQuery {
    markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
      frontmatter {
        news_carousel {
          title
          content
          img_url {
            publicURL
          }
          button_content
          button_link
        }
      }
    }
  }
`;
